<script setup lang="ts">
import { cn } from '~ui/util/tailwind'
import { whenever } from '@vueuse/core'

import type { CarouselEmits, CarouselProps, WithClassAsProps } from './interface'

import { useProvideCarousel } from './useCarousel'

const props = withDefaults(defineProps<CarouselProps & WithClassAsProps>(), {
  orientation: 'horizontal',
})

const emits = defineEmits<CarouselEmits>()

const { canScrollNext, canScrollPrev, carouselApi, carouselRef, orientation, scrollNext, scrollPrev } = useProvideCarousel(props, emits)

const syncedScrollSnapIndex = ref(carouselApi.value?.selectedScrollSnap() ?? unref(props.opts)?.startIndex ?? 0)

defineExpose({
  scrollNext,
  scrollPrev,
  carouselApi,
  carouselRef,
  orientation,
  canScrollNext,
  canScrollPrev,
  selectedScrollSnapIndex: syncedScrollSnapIndex,
})

whenever(carouselApi, () => {
  syncedScrollSnapIndex.value = carouselApi.value!.selectedScrollSnap()
  carouselApi.value!.on('select', ({ selectedScrollSnap }) => {
    syncedScrollSnapIndex.value = selectedScrollSnap()
  })
}, { once: true })

const onKeyDown = (event: KeyboardEvent) => {
  const elementsToIgnore = [HTMLInputElement, HTMLTextAreaElement, HTMLSelectElement, HTMLButtonElement]
  const isElementEditable = (element: EventTarget | null) => (element instanceof Element && element.hasAttribute('contenteditable'))

  if (elementsToIgnore.some(element => event.target instanceof element) || isElementEditable(event.target))
    return

  const prevKey = props.orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft'
  const nextKey = props.orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight'

  if (event.key === prevKey) {
    event.preventDefault()
    scrollPrev()

    return
  }

  if (event.key === nextKey) {
    event.preventDefault()
    scrollNext()
  }
}
</script>

<template>
  <div
    :class="cn('relative !outline-none', props.class)"
    role="region"
    aria-roledescription="carousel"
    tabindex="0"
    @keydown="onKeyDown"
  >
    <slot
      :orientation
      :scroll-next
      :scroll-prev
      :carousel-api
      :carousel-ref
      :can-scroll-next
      :can-scroll-prev
      :selected-scroll-snap-index="syncedScrollSnapIndex"
    />
  </div>
</template>
